import { Box3, Plane, Vector3 } from 'three'

/**
 * Get points of the polygon formed by the intersection of a box and a plane.
 *
 * @param box Bounding box
 * @param plane Intersection plane
 */
export const getPlaneQuadInBox = (box: Box3, plane: Plane) => {
  const corners = [
    new Vector3(box.min.x, box.min.y, box.min.z),
    new Vector3(box.max.x, box.min.y, box.min.z),
    new Vector3(box.max.x, box.max.y, box.min.z),
    new Vector3(box.min.x, box.max.y, box.min.z),

    new Vector3(box.min.x, box.min.y, box.max.z),
    new Vector3(box.max.x, box.min.y, box.max.z),
    new Vector3(box.max.x, box.max.y, box.max.z),
    new Vector3(box.min.x, box.max.y, box.max.z),
  ]

  // prettier-ignore
  const edgePairs = [
    [0,1],[1,2],[2,3],[3,0],
    [4,5],[5,6],[6,7],[7,4],
    [0,4],[1,5],[2,6],[3,7],
  ];

  const intersections = []
  const a = new Vector3()
  const b = new Vector3()
  const hit = new Vector3()

  // edge-plane intersections
  for (let i = 0; i < edgePairs.length; i += 1) {
    a.copy(corners[edgePairs[i][0]])
    b.copy(corners[edgePairs[i][1]])

    const da = plane.distanceToPoint(a)
    const db = plane.distanceToPoint(b)

    if (da * db < 0) {
      const t = da / (da - db)
      hit.copy(a).lerp(b, t)
      intersections.push(hit.clone())
    }
  }

  if (intersections.length < 3) return null

  // -------------------------
  // Sort points on the plane
  // -------------------------

  // centroid
  const center = new Vector3()
  for (let i = 0; i < intersections.length; i += 1) {
    center.add(intersections[i])
  }
  center.multiplyScalar(1 / intersections.length)

  // build orthonormal basis
  const normal = plane.normal.clone().normalize()
  const tangent = new Vector3()

  // pick a vector not parallel to normal
  if (Math.abs(normal.x) > 0.9) {
    tangent.set(0, 1, 0) // use Y if X is dominant
  } else {
    tangent.set(1, 0, 0) // otherwise use X
  }

  // project tangent onto plane: T = T - (T·N)N
  tangent.sub(normal.clone().multiplyScalar(tangent.dot(normal))).normalize()

  // B = N × T
  const bitangent = new Vector3().crossVectors(normal, tangent)

  // sort by angle
  intersections.sort((p1, p2) => {
    const v1 = p1.clone().sub(center)
    const v2 = p2.clone().sub(center)

    const a1 = Math.atan2(v1.dot(bitangent), v1.dot(tangent))
    const a2 = Math.atan2(v2.dot(bitangent), v2.dot(tangent))

    return a1 - a2
  })

  // -------------------------
  // Ensure exactly 4 points
  // -------------------------
  if (intersections.length === 4) return intersections

  // If more than 4 (usually 5 or 6), pick farthest 4 from center
  intersections.sort((p1, p2) => center.distanceTo(p2) - center.distanceTo(p1))

  return intersections.slice(0, 4)
}
